import React, { useState, useEffect, useRef } from 'react';

// material-ui
import { Alert, Box, Button, CssBaseline, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell, TableRow, Tabs, TextField, Typography } from '@mui/material';

//components
import AlertInfo from '../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../components/elements/AlertInfoDialog';
import TopBar from '../../components/nav/TopBar';
import LetterType, { LetterDocumentPreview, LetterField, LetterValue } from './dataObjects/LetterType';
import LetterFieldInput from './components/LetterFieldInput';
import DocxPreview from './components/DocxPreview';

const PREVIEW_DELAY_MS = 500;

const LegalLettersApp = (): JSX.Element => {

    //set up browser title
    const pageTitle = 'Extranet - Legal Letters';
    document.title = pageTitle;

    const applicationName = 'Legal Letters';

    //set up state for the app
    const [letterTypes, setLetterTypes] = useState<LetterType[]>([]);
    const [letterTypeKey, setLetterTypeKey] = useState('');
    // keyed by field key; kept across letter types so shared fields carry over
    const [values, setValues] = useState<Record<string, string>>({});
    // what each document is filled with, from the server, caught up PREVIEW_DELAY_MS after typing stops
    const [preview, setPreview] = useState<LetterDocumentPreview[]>([]);
    const [previewError, setPreviewError] = useState('');
    // only the latest preview request is shown, in case an earlier one answers late
    const latestPreviewRequest = useRef(0);
    const [showErrors, setShowErrors] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    // the documents waiting on the user to confirm downloading with required fields blank
    const [pendingDownload, setPendingDownload] = useState<string[] | null>(null);
    const [documentTab, setDocumentTab] = useState(0);

    const letterType = letterTypes.find(t => t.key === letterTypeKey);
    // a field waived by a checked box ("No specific terms") is neither required nor used
    const isWaived = (field: LetterField) => !!field.waivedBy && values[field.waivedBy] === 'true';
    const missingFields = letterType ? letterType.fields.filter(f => f.isRequired && !isWaived(f) && !(values[f.key] ?? '').trim()) : [];

    //payload
    useEffect(() => {
        getPayload();
    }, []);

    // live preview: refresh once typing pauses rather than on every keystroke
    useEffect(() => {
        if (!letterTypeKey)
            return;
        const timer = setTimeout(() => getPreview(letterTypeKey, values), PREVIEW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [letterTypeKey, values]);

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const showAlert = (type: string, title: string, message: string, secondaryMessage?: string) => {
        setAlertInfo({
            type: type,
            title: title,
            errorMessage: message,
            secondaryErrorMessage: secondaryMessage ?? ''
        });
        setAlertInfoDialog(true);
    }

    const getPayload = async () => {
        await fetch('/LegalLetters/GetPayload')
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    showAlert('error', 'Error: Legal Letters', result.errorMessage);
                    return;
                }
                setLetterTypes(result.letterTypes);
                // with a single letter type there is nothing to choose
                if (result.letterTypes.length === 1) {
                    setLetterTypeKey(result.letterTypes[0].key);
                }
            })
    }

    // the server fills each document that has a template and sends it back as a .docx to display
    const getPreview = (key: string, entered: Record<string, string>) => {
        const requestId = ++latestPreviewRequest.current;
        fetch('/LegalLetters/Preview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ letterTypeKey: key, values: entered })
        })
            .then(result => result.ok ? result.json() : { isError: true, errorMessage: result.statusText })
            .then(result => {
                if (requestId !== latestPreviewRequest.current)
                    return;
                if (result.isError) {
                    setPreviewError(result.errorMessage || 'Could not build the preview.');
                    return;
                }
                setPreviewError('');
                setPreview(result.result);
            })
            .catch(() => {
                if (requestId === latestPreviewRequest.current)
                    setPreviewError('Could not reach the server for the preview.');
            });
    }

    const selectLetterType = (key: string) => {
        setLetterTypeKey(key);
        setShowErrors(false);
        setDocumentTab(0);
    }

    const changeValue = (key: string, value: string) => {
        setValues(current => ({ ...current, [key]: value }));
    }

    const clearForm = () => {
        setValues({});
        setShowErrors(false);
    }

    // the required check happens here rather than on the preview, which shows blanks as placeholders;
    // the values go in the request body, never the URL, since they are summons details
    // with required fields blank, the user confirms first; the blanks download as highlighted [Label]s
    const download = (documentKeys: string[]) => {
        if (missingFields.length > 0) {
            setShowErrors(true);
            setPendingDownload(documentKeys);
            return;
        }
        saveDocuments(documentKeys);
    }

    const confirmDownload = () => {
        const documentKeys = pendingDownload ?? [];
        setPendingDownload(null);
        saveDocuments(documentKeys);
    }

    // "Download all" saves each document as its own file, one after another
    const saveDocuments = async (documentKeys: string[]) => {
        setIsDownloading(true);
        try {
            for (const documentKey of documentKeys) {
                const result = await fetch('/LegalLetters/Download', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ letterTypeKey: letterTypeKey, documentKey: documentKey, values: values })
                });
                // a refusal comes back as JSON, a document as a file
                if (!result.ok || (result.headers.get('Content-Type') ?? '').includes('json')) {
                    const outcome = result.ok ? await result.json() : { errorMessage: result.statusText };
                    showAlert('error', 'Download', outcome.errorMessage || 'Could not build the download.');
                    return;
                }
                saveFile(await result.blob(), fileNameFrom(result.headers.get('Content-Disposition')) ?? 'letter.docx');
            }
        }
        catch {
            showAlert('error', 'Download', 'Could not reach the server for the download.');
        }
        finally {
            setIsDownloading(false);
        }
    }

    const saveFile = (blob: Blob, fileName: string) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
    }

    // filename*=UTF-8''... when the name has non-ASCII characters, otherwise filename=...
    const fileNameFrom = (disposition: string | null) => {
        if (!disposition)
            return null;
        const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
        if (encoded)
            return decodeURIComponent(encoded[1]);
        const plain = /filename="?([^";]+)"?/i.exec(disposition);
        return plain ? plain[1] : null;
    }

    // fields in definition order, grouped under their headings
    const fieldGroups = (fields: LetterField[]) => {
        const groups: { name: string, fields: LetterField[] }[] = [];
        fields.forEach(field => {
            const name = field.group ?? '';
            let group = groups.find(g => g.name === name);
            if (!group) {
                group = { name: name, fields: [] };
                groups.push(group);
            }
            group.fields.push(field);
        });
        return groups;
    }

    const currentDocumentKey = letterType?.documents[documentTab]?.key ?? '';
    const currentPreview = preview.find(p => p.key === currentDocumentKey);

    // a blank value previews as a highlighted [Label] so the user can see where it lands
    const previewValue = (item: LetterValue) => {
        if (!item.value && !item.isRequired) {
            return <Box component="span" sx={{ color: 'text.secondary' }}>(none)</Box>;
        }
        if (!item.value) {
            return <Box component="span" sx={{ backgroundColor: '#fff3b0', paddingX: 0.5 }}>[{item.label}]</Box>;
        }
        return item.value;
    }

    return (
        // on wide screens the page fills the window and the form and preview scroll separately,
        // so the preview stays in view while the form is filled in; narrow screens stack and scroll as one
        <Box sx={{ display: 'flex', flexDirection: 'column', height: { md: '100vh' } }}>
            <Grid container sx={{ display: 'flex' }}>
                <CssBaseline />
                <TopBar applicationName={applicationName}></TopBar>
            </Grid>
            <Grid item xs={12} marginBottom={1}>
                <Divider />
            </Grid>
            <Box sx={{ flex: 1, minHeight: 0, display: { md: 'flex' }, gap: 4, padding: '22px 40px 15px 40px' }}>

                {/* letter type and fields */}
                <Box sx={{ width: { md: '40%' }, flexShrink: 0, overflowY: { md: 'auto' }, paddingTop: 1, paddingRight: { md: 2 } }}>
                    <TextField
                        select
                        label="Letter type"
                        size="small"
                        fullWidth
                        value={letterTypeKey}
                        onChange={(e) => selectLetterType(e.target.value)}
                    >
                        {letterTypes.map(t => <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>)}
                    </TextField>
                    {
                        letterType?.description &&
                        <Typography variant="body2" color="text.secondary" marginTop={1}>{letterType.description}</Typography>
                    }

                    {
                        letterType && fieldGroups(letterType.fields).map(group =>
                            <Box key={group.name} marginTop={3}>
                                {group.name && <Typography variant="subtitle1" fontWeight={550} marginBottom={1.5}>{group.name}</Typography>}
                                <Grid container spacing={2}>
                                    {group.fields.map(field =>
                                        <Grid item key={field.key} xs={12} sm={field.width}>
                                            <LetterFieldInput
                                                field={field}
                                                value={values[field.key] ?? ''}
                                                showError={showErrors && missingFields.includes(field)}
                                                disabled={isWaived(field)}
                                                onChange={changeValue}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )
                    }

                    {
                        letterType &&
                        <Stack direction="row" spacing={2} marginTop={4}>
                            <Button onClick={clearForm}>Clear</Button>
                        </Stack>
                    }
                    {
                        showErrors && missingFields.length > 0 &&
                        <Alert severity="warning" sx={{ marginTop: 2 }}>Some required fields are blank. They will show in the download as highlighted placeholders.</Alert>
                    }
                </Box>

                {/* preview */}
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', paddingTop: 1, marginTop: { xs: 4, md: 0 } }}>
                    {
                        letterType &&
                        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" paddingRight={2}>
                                <Tabs value={documentTab} onChange={(e, tab) => setDocumentTab(tab)}>
                                    {letterType.documents.map(d => <Tab key={d.key} label={d.name} />)}
                                </Tabs>
                                <Stack direction="row" spacing={1}>
                                    <Button size="small" variant="outlined" disabled={isDownloading || !currentPreview?.docx}
                                        onClick={() => download([currentDocumentKey])}>Download</Button>
                                    <Button size="small" variant="outlined" disabled={isDownloading || !preview.some(p => p.docx)}
                                        onClick={() => download(preview.filter(p => p.docx).map(p => p.key))}>Download all</Button>
                                </Stack>
                            </Stack>
                            <Divider />
                            <Box padding={currentPreview?.docx ? 0 : 3} sx={{ overflowY: 'auto', minHeight: { xs: 500, md: 0 }, position: 'relative' }}>
                                {previewError && <Alert severity="error" sx={{ margin: currentPreview?.docx ? 2 : 0, marginBottom: 2 }}>{previewError}</Alert>}
                                {
                                    currentPreview?.docx ?
                                        <DocxPreview docx={currentPreview.docx} onError={setPreviewError} />
                                        :
                                        <>
                                            <Alert severity="info" sx={{ marginBottom: 2 }}>
                                                The {letterType.documents[documentTab]?.name.toLowerCase()} template hasn't been added yet. These are the values that will fill it.
                                            </Alert>
                                            <Table size="small">
                                                <TableBody>
                                                    {(currentPreview?.values ?? []).map(item =>
                                                        <TableRow key={item.key}>
                                                            <TableCell width="35%"><Typography variant="body2" fontWeight={550}>{item.label}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{previewValue(item)}</Typography></TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </>
                                }
                            </Box>
                        </Paper>
                    }
                </Box>
            </Box>
            <Dialog open={pendingDownload !== null} onClose={() => setPendingDownload(null)}>
                <DialogTitle>Required fields are blank</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        These fields haven't been filled in. They will show in the {pendingDownload?.length === 1 ? 'document' : 'documents'} as highlighted placeholders, like [Name]:
                    </DialogContentText>
                    <Box component="ul" sx={{ marginBottom: 0 }}>
                        {/* "Name" is under both Recipient and Signed for OCPF by, so say which */}
                        {missingFields.map(f => <li key={f.key}><Typography variant="body2">{f.group ? `${f.group}: ${f.label}` : f.label}</Typography></li>)}
                    </Box>
                    <DialogContentText>Download anyway?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDownload(null)}>Go back</Button>
                    <Button variant="contained" onClick={confirmDownload}>Download anyway</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog
                    isAlertInfoDialogOpen={isAlertInfoDialogOpen}
                    alertInfo={alertInfo}
                    closeAlertInfoDialog={closeAlertInfoDialog}
                ></AlertInfoDialog>
            }
        </Box>
    );
};

export default LegalLettersApp;
